import useAxios from 'axios-hooks';
import { defaults, get, intersection, omit } from 'lodash-es';
import React, { useCallback, useState } from 'react';
import * as yup from 'yup';
import {
  VisitResponse,
  MasterInfo,
  ServiceGroupsInfo,
  FormValues,
  FirstStepType,
  IntervalsData,
  SlotsPerMaster,
} from 'types';
import { Loader, Wizard, WizardPage } from 'components';
import { AxiosError } from 'axios';
import { EXTERNAL_PROVIDERS } from 'constants/externalProviders';
import { format } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { DATE_TIME_FORMAT } from 'constants/dates';
import { Contacts } from './Contacts';
import { Master } from './Master';
import { Service } from './Service';
import { TimeSlot } from './TimeSlot';
import { useSearchParams } from '../../hooks';

const phoneErrorMessage = 'Введите номер телефона в формате +7(XXX) XXX-XX-XX';

const contactsValidationSchema = yup.object().shape({
  clientInfo: yup.object().shape({
    name: yup.string().required('Введите имя'),
    phone: yup
      .string()
      .matches(/^(\+)(\d)(\()(\d{3})(\))(\s)(\d{3})(-)(\d{2})(-)(\d{2})$/g, {
        message: phoneErrorMessage,
      })
      .required(phoneErrorMessage),
    comment: yup.string(),
  }),
});

const masterValidationSchema = yup.object().shape({
  employeeId: yup.string().required(),
});

const serviceValidationSchema = yup.object().shape({
  favorIds: yup.array().min(1).required(),
});

const timeSlotValidationSchema = yup.object().shape({
  date: yup.string().required(),
});

const dayValidationSchema = yup.object().shape({
  day: yup.string().required(),
});

export const FormPageIndividual: React.FC<{
  initialValues: Partial<FormValues>;
  firstStep: FirstStepType;
  address: string;
  onFinish: (response: VisitResponse) => void;
  onExit: () => void;
  dataLoading: boolean;
  dataError?: AxiosError<unknown>;
  data: {
    mastersData: MasterInfo[];
    servicesData: ServiceGroupsInfo;
    hasRecordingFromService: boolean;
    hasRecordingFromMaster: boolean;
    specialistCases: {
      specialistNominativeCase: string;
      specialistGenitiveCase: string;
      specialistPrepositionalCase: string;
    };
    fieldNameLabel: string;
    fieldCommentLabel: string;
    maxVisitDuration: string;
  };
  duration: string;
  divisionId: string | null;
  timezone?: string;
  nameButtonMaster?: string;
  nameButtonService?: string;
  isBookingFromVk: boolean;
}> = ({
  initialValues,
  firstStep,
  address,
  onFinish,
  onExit,
  dataLoading,
  dataError,
  data: {
    mastersData,
    servicesData,
    hasRecordingFromMaster,
    hasRecordingFromService,
    specialistCases,
    fieldNameLabel,
    fieldCommentLabel,
    maxVisitDuration,
  },
  duration,
  divisionId,
  timezone,
  nameButtonMaster,
  nameButtonService,
  isBookingFromVk,
}) => {
  const source =
    useSearchParams('source', EXTERNAL_PROVIDERS) ||
    (isBookingFromVk ? 'vk_system' : null);
  const { specialistGenitiveCase } = specialistCases;

  const [errorSeen, setErrorSeen] = useState<AxiosError<unknown>>();

  const [possibleMastersIds, setPossibleMastersIds] = useState<
    string[] | undefined
  >();

  const [possibleMastersByDayIds, setPossibleMastersByDayIds] = useState<
    string[] | undefined
  >();

  const [slotsPerMaster, setSlotsPerMaster] = useState<
    SlotsPerMaster[] | undefined
  >();

  const [intervalsFavor, setIntervalsFavor] = useState<
    IntervalsData | undefined
  >();

  const [{ loading: formLoading, error: formError }, postVisit] =
    useAxios<VisitResponse>(
      {
        method: 'POST',
        url: '/booking-widget/client/visit',
      },
      {
        manual: true,
      },
    );

  const loading = formLoading || dataLoading;
  const error = dataError || formError;

  const onSubmit = (values: FormValues) => {
    const favorCounts = values.favorIds.reduce(
      (prev: Record<string, number>, id) => {
        const newCount = prev;
        newCount[id] = values.favorCounts?.[id] ?? 1;
        return newCount;
      },
      {},
    );

    // Если был выбран пункт "Любой мастер", то берём значение для employeeId
    // из masterId. Затем отбрасываем служебные поля day и masterId.
    const formValues = omit(
      defaults(
        {
          employeeId: values.employeeId === 'any' ? values.masterId : undefined,
          // подставляем таймзону филиала, если она была указана
          date: timezone
            ? toDate(format(values.date, DATE_TIME_FORMAT), {
                timeZone: timezone,
              })
            : undefined,
          favorCounts,
        },
        values,
      ),
      ['day', 'masterId', 'groupEventInfo'],
    );

    const { name, phone = '', comment } = values.clientInfo;

    return postVisit({
      data: {
        ...formValues,
        ...(divisionId ? { divisionId } : {}),
        clientInfo: {
          name,
          phone: phone.replace(/[()\s-]/g, ''),
        },
        comment,
        ...(source ? { source } : {}),
      },
    }).then((res) => {
      const visitId = get(res, 'data.id');
      if (visitId)
        window.history.replaceState(
          null,
          '',
          `?visitId=${visitId}&visitType=individual`,
        );
      onFinish(res.data);
    });
  };

  const onErrorClose = useCallback(() => {
    setErrorSeen(error);
  }, [error]);

  const intersectionPossible = () => {
    if (!possibleMastersIds?.length) {
      return possibleMastersByDayIds;
    }
    if (!possibleMastersByDayIds?.length) {
      return possibleMastersIds;
    }
    return intersection(possibleMastersIds, possibleMastersByDayIds);
  };

  const steps = [
    <WizardPage
      title="Выбор услуг"
      validationSchema={serviceValidationSchema}
      name="favorIds"
      key="favorIds"
      loading={dataLoading}
    >
      <Service
        name="favorIds"
        serviceGroups={servicesData}
        possibleMastersIds={intersectionPossible()}
        possibleMastersByDayIds={possibleMastersByDayIds}
        setPossibleMastersIds={setPossibleMastersIds}
        widgetId={initialValues.widgetId}
        slotsPerMaster={slotsPerMaster}
        setSlotsPerMaster={setSlotsPerMaster}
        duration={duration}
        intervalsFavor={intervalsFavor}
        isFirstStepDate={firstStep === 'date'}
        maxVisitDuration={maxVisitDuration}
      />
    </WizardPage>,
    <WizardPage
      title={`Выбор ${specialistGenitiveCase}`}
      validationSchema={masterValidationSchema}
      name="employeeId"
      key="employeeId"
      loading={dataLoading}
    >
      <Master
        name="employeeId"
        masters={mastersData}
        possibleMastersIds={intersectionPossible()}
        serviceGroups={servicesData}
        widgetId={initialValues.widgetId}
        isPossibleMastersExist={typeof possibleMastersIds !== 'undefined'}
        possibleMastersByDayIds={possibleMastersByDayIds}
        setPossibleMastersIds={setPossibleMastersIds}
        specialistCases={specialistCases}
      />
    </WizardPage>,
  ];

  const [secondStep, setSecondStep] = useState<FirstStepType | null>(null);

  // Пропускаем экран выбора сотрудника, если значение уже установлено
  if (initialValues.employeeId) steps.pop();

  const isServiceSelected = firstStep === 'service' || secondStep === 'service';

  const childs = isServiceSelected ? steps : steps.reverse();

  return (
    <>
      <Loader
        loading={loading}
        error={error !== errorSeen ? error : false}
        onErrorClose={onErrorClose}
      />
      <Wizard initialValues={initialValues} onSubmit={onSubmit} onExit={onExit}>
        {firstStep === 'date' && (
          <WizardPage
            title="Выбор даты"
            validationSchema={dayValidationSchema}
            name="day"
          >
            <TimeSlot
              name="day"
              serviceGroups={servicesData}
              possibleMastersIds={mastersData.map((master) => master.id)}
              masters={mastersData}
              dayOnly
              hasRecordingFromService={hasRecordingFromService}
              hasRecordingFromMaster={hasRecordingFromMaster}
              setSecondStep={setSecondStep}
              setSlotsPerMaster={setSlotsPerMaster}
              setIntervalsFavor={setIntervalsFavor}
              setPossibleMastersByDayIds={setPossibleMastersByDayIds}
              specialistCases={specialistCases}
              divisionId={divisionId}
              timezone={timezone}
              nameButtonService={nameButtonService}
              nameButtonMaster={nameButtonMaster}
            />
          </WizardPage>
        )}

        {childs}

        <WizardPage
          title={
            firstStep === 'date' ? 'Выбор времени' : 'Выбор даты и времени'
          }
          validationSchema={timeSlotValidationSchema}
          name="date"
        >
          <TimeSlot
            name="date"
            serviceGroups={servicesData}
            possibleMastersIds={intersectionPossible()}
            masters={mastersData}
            setPossibleMastersByDayIds={setPossibleMastersByDayIds}
            setSlotsPerMaster={setSlotsPerMaster}
            timeOnly={firstStep === 'date'}
            specialistCases={specialistCases}
            divisionId={divisionId}
            timezone={timezone}
            serviceSelected={isServiceSelected}
          />
        </WizardPage>
        <WizardPage
          title="Подтверждение записи"
          name="clientInfo"
          validationSchema={contactsValidationSchema}
        >
          <Contacts
            widgetId={initialValues.widgetId}
            address={address}
            masters={mastersData}
            possibleMastersIds={intersectionPossible()}
            serviceGroups={servicesData}
            slotsPerMaster={slotsPerMaster}
            specialistCases={specialistCases}
            fieldNameLabel={fieldNameLabel}
            fieldCommentLabel={fieldCommentLabel}
          />
        </WizardPage>
      </Wizard>
    </>
  );
};
